/**
 * Shared email layout components (header, footer, helpers).
 * Reusable across all TrustJet email templates.
 */

import { TrustJetEmailBackground, TrustJetLogoWhite } from '@/assets/images';
import { resolveAssetUrl } from '@/utils/urls';

// Inline SVG icon helper (data URI for email compatibility)
const RI_PATHS = {
  plane: 'M14 8.94737L22 14V16L14 13.4737V18.8333L17 20.5V22L12.5 21L8 22V20.5L11 18.8333V13.4737L3 16V14L11 8.94737V3.5C11 2.67157 11.6716 2 12.5 2C13.3284 2 14 2.67157 14 3.5V8.94737Z',
  check: 'M11.602 13.7599L13.014 15.1719L21.4795 6.7063L22.8938 8.12051L13.014 18.0003L6.65 11.6363L8.06421 10.2221L10.189 12.3469L11.602 13.7599ZM11.6037 10.9322L16.5563 5.97949L17.9666 7.38977L13.014 12.3424L11.6037 10.9322ZM8.77698 16.5873L7.36396 18.0003L1 11.6363L2.41421 10.2221L3.82723 11.6352L3.82604 11.6363L8.77698 16.5873Z',
  trophy: 'M13.0049 16.9409V19.0027H18.0049V21.0027H6.00488V19.0027H11.0049V16.9409C7.05857 16.4488 4.00488 13.0824 4.00488 9.00275V3.00275H20.0049V9.00275C20.0049 13.0824 16.9512 16.4488 13.0049 16.9409ZM6.00488 5.00275V9.00275C6.00488 12.3165 8.69117 15.0027 12.0049 15.0027C15.3186 15.0027 18.0049 12.3165 18.0049 9.00275V5.00275H6.00488ZM1.00488 5.00275H3.00488V9.00275H1.00488V5.00275ZM21.0049 5.00275H23.0049V9.00275H21.0049V5.00275Z',
  medal: 'M12 6.99999C16.4183 6.99999 20 10.5817 20 15C20 19.4183 16.4183 23 12 23C7.58172 23 4 19.4183 4 15C4 10.5817 7.58172 6.99999 12 6.99999ZM12 8.99999C8.68629 8.99999 6 11.6863 6 15C6 18.3137 8.68629 21 12 21C15.3137 21 18 18.3137 18 15C18 11.6863 15.3137 8.99999 12 8.99999ZM12 10.5L13.3225 13.1797L16.2798 13.6094L14.1399 15.6953L14.645 18.6406L12 17.25L9.35497 18.6406L9.86012 15.6953L7.72025 13.6094L10.6775 13.1797L12 10.5ZM18 1.99999V4.99999L16.6366 6.13755C15.5305 5.5577 14.3025 5.17884 13.0011 5.04948L13 1.99899L18 1.99999ZM11 1.99899L10.9997 5.04939C9.6984 5.17863 8.47046 5.55735 7.36441 6.13703L6 4.99999V1.99999L11 1.99899Z',
  priceTag: 'M10.9042 2.10025L20.8037 3.51446L22.2179 13.414L13.0255 22.6063C12.635 22.9969 12.0019 22.9969 11.6113 22.6063L1.71184 12.7069C1.32131 12.3163 1.32131 11.6832 1.71184 11.2926L10.9042 2.10025ZM11.6113 4.22157L3.83316 11.9997L12.3184 20.485L20.0966 12.7069L19.036 5.28223L11.6113 4.22157ZM13.7327 10.5855C12.9516 9.80448 12.9516 8.53815 13.7327 7.7571C14.5137 6.97606 15.78 6.97606 16.5611 7.7571C17.3421 8.53815 17.3421 9.80448 16.5611 10.5855C15.78 11.3666 14.5137 11.3666 13.7327 10.5855Z',
  building: 'M21 19H23V21H1V19H3V4C3 3.44772 3.44772 3 4 3H14C14.5523 3 15 3.44772 15 4V19H19V11H17V9H20C20.5523 9 21 9.44772 21 10V19ZM5 5V19H13V5H5ZM7 11H11V13H7V11ZM7 7H11V9H7V7Z',
  team: 'M2 22C2 17.5817 5.58172 14 10 14C14.4183 14 18 17.5817 18 22H16C16 18.6863 13.3137 16 10 16C6.68629 16 4 18.6863 4 22H2ZM10 13C6.685 13 4 10.315 4 7C4 3.685 6.685 1 10 1C13.315 1 16 3.685 16 7C16 10.315 13.315 13 10 13ZM10 11C12.21 11 14 9.21 14 7C14 4.79 12.21 3 10 3C7.79 3 6 4.79 6 7C6 9.21 7.79 11 10 11ZM18.2837 14.7028C21.0644 15.9561 23 18.752 23 22H21C21 19.564 19.5483 17.4671 17.4628 16.5271L18.2837 14.7028ZM17.5962 3.41321C19.5944 4.23703 21 6.20361 21 8.5C21 10.7964 19.5944 12.763 17.5962 13.5868L16.8197 11.7639C18.1236 11.2141 19 9.95731 19 8.5C19 7.04269 18.1236 5.78591 16.8197 5.2361L17.5962 3.41321Z',
  calendar: 'M9 1V3H15V1H17V3H21C21.5523 3 22 3.44772 22 4V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H7V1H9ZM20 11H4V19H20V11ZM8 13V15H6V13H8ZM13 13V15H11V13H13ZM18 13V15H16V13H18ZM7 5H4V9H20V5H17V7H15V5H9V7H7V5Z',
  arrowRight: 'M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z',
  mapPin: 'M12 20.8995L16.9497 15.9497C19.6834 13.2161 19.6834 8.78392 16.9497 6.05025C14.2161 3.31658 9.78392 3.31658 7.05025 6.05025C4.31658 8.78392 4.31658 13.2161 7.05025 15.9497L12 20.8995ZM12 23.7279L5.63604 17.364C2.12132 13.8492 2.12132 8.15076 5.63604 4.63604C9.15076 1.12132 14.8492 1.12132 18.364 4.63604C21.8787 8.15076 21.8787 13.8492 18.364 17.364L12 23.7279ZM12 13C13.1046 13 14 12.1046 14 11C14 9.89543 13.1046 9 12 9C10.8954 9 10 9.89543 10 11C10 12.1046 10.8954 13 12 13ZM12 15C9.79086 15 8 13.2091 8 11C8 8.79086 9.79086 7 12 7C14.2091 7 16 8.79086 16 11C16 13.2091 14.2091 15 12 15Z',
  time: 'M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20ZM13 12H17V14H11V7H13V12Z',
  flightTakeoff: 'M21.949 10.1118C22.1634 10.912 21.6886 11.7345 20.8884 11.9489L5.2218 16.1467C4.77856 16.2655 4.31138 16.0674 4.08866 15.6662L1.46582 10.9415L2.91471 10.5533L5.3825 12.9979L10.4778 11.6326L5.96728 4.55896L7.89913 4.04132L14.8505 10.4609L20.1119 9.05113C20.9121 8.83671 21.7346 9.31159 21.949 10.1118ZM4.00013 19H20.0001V21H4.00013V19Z',
  flightLand: 'M20.949 14.8882C20.7346 15.6884 19.9121 16.1633 19.1119 15.9489L3.44528 11.751C3.00205 11.6323 2.69653 11.2271 2.70424 10.7683L2.79514 5.36515L4.24403 5.75338L5.15891 9.10435L10.2542 10.4696L9.88479 2.08844L11.8166 2.60608L14.6269 11.6413L19.8883 13.0511C20.6885 13.2655 21.1634 14.088 20.949 14.8882ZM4.00008 19H20.0001V21H4.00008V19Z',
  facebook: 'M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z',
  twitter: 'M18.2048 2.25H21.5128L14.2858 10.51L22.7878 21.75H16.1308L10.9158 14.933L4.95084 21.75H1.64084L9.37084 12.915L1.21484 2.25H8.04084L12.7538 8.481L18.2048 2.25ZM17.0438 19.77H18.8768L7.04484 4.126H5.07784L17.0438 19.77Z',
  linkedin: 'M18.335 18.339H15.67v-4.177c0-.996-.02-2.278-1.39-2.278-1.389 0-1.601 1.084-1.601 2.205v4.25h-2.666V9.75h2.56v1.17h.035c.358-.674 1.228-1.387 2.528-1.387 2.7 0 3.2 1.778 3.2 4.091v4.715zM7.003 8.575a1.546 1.546 0 01-1.548-1.549 1.548 1.548 0 111.547 1.549zm1.336 9.764H5.666V9.75H8.34v8.589zM19.67 3H4.329C3.593 3 3 3.58 3 4.297v15.406C3 20.42 3.594 21 4.328 21h15.338C20.4 21 21 20.42 21 19.703V4.297C21 3.58 20.4 3 19.666 3h.003z',
  userAdd: 'M14 14.252V22H4C4 17.5817 7.58172 14 12 14C12.6906 14 13.3608 14.0875 14 14.252ZM12 13C8.685 13 6 10.315 6 7C6 3.685 8.685 1 12 1C15.315 1 18 3.685 18 7C18 10.315 15.315 13 12 13ZM12 11C14.21 11 16 9.21 16 7C16 4.79 14.21 3 12 3C9.79 3 8 4.79 8 7C8 9.21 9.79 11 12 11ZM18 17V14H20V17H23V19H20V22H18V19H15V17H18Z',
  editDocument: 'M20 2C20.5523 2 21 2.44772 21 3V6.757L19 8.757V4H5V20H19V17.242L21 15.242V21C21 21.5523 20.5523 22 20 22H4C3.44772 22 3 21.5523 3 21V3C3 2.44772 3.44772 2 4 2H20ZM21.7782 8.80761L23.1924 10.2218L15.4142 18L13.9979 17.9979L14 16.5858L21.7782 8.80761ZM13 12V14H8V12H13ZM16 8V10H8V8H16Z',
};

const baseUrl = process.env.REACT_APP_ENVIRONMENT=="LOCAL" ? window.location.origin : (process.env.REACT_APP_ENVIRONMENT=="PROD" ? "https://app.trustjet.ai" : "https://dev.trustjet.ai");

export const icon = (name, size = 16) => {
  if (!RI_PATHS[name]) return '';
  return `<img src="${baseUrl}/images/icons/${name}.svg" width="${size}" height="${size}" alt="" style="display:inline-block;vertical-align:middle;" />`;
};

export const spacer = (height = 20) =>
  `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;"><tr><td style="height:${height}px;line-height:${height}px;font-size:0;">&nbsp;</td></tr></table>`;

/**
 * Email document open (DOCTYPE, <head>, <body> open, outer table open)
 * @param {Object} options
 * @param {string} [options.title] - <title> tag content
 */
export const emailDocOpen = ({ title = 'TrustJet' } = {}) => `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <link href="https://fonts.googleapis.com/css2?family=Rethink+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
</head>
<body style="margin:0;padding:0;background-color:#F7F7F7; font-family:'Rethink Sans',sans-serif;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#F7F7F7;">
    <tr>
      <td align="center" style="padding:24px 16px;">`;

/**
 * Email document close (outer table close, </body>, </html>)
 */
export const emailDocClose = () => `
      </td>
    </tr>
  </table>
</body>
</html>`;

/**
 * Email header block with background image, logo, subtitle and title.
 * @param {Object} options
 * @param {string} options.title - Main heading text
 * @param {string} [options.subtitle] - Smaller text above the title
 */
export const emailHeader = ({ title, subtitle = '', organization, isBroker = false } = {}) => {
  const logoSrc = organization?.branding?.logo;
  const orgName = organization?.name || 'TrustJet';
  const brandColor = organization?.branding?.color || '#1B4332';
  const logoHtml = isBroker
    ? `<img src="${window.location.origin}/images/whitelogoslunt.png" alt="TrustJet" width="160" style="display:block;width:160px;height:auto;" />`
    : (logoSrc
      ? `<img src="${resolveAssetUrl(logoSrc)}" alt="${orgName}" width="160" style="display:block;width:160px;height:auto;" />`
      : `<h2 style="margin:0;font-size:22px;font-weight:700;color:#FFFFFF;">${orgName}</h2>`);
  const bgStyle = isBroker
    ? `background-image:url('${window.location.origin}/images/bgemail.jpg');background-size:cover;background-position:center bottom;`
    : `background-color:${brandColor};`;
  return ` 
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;">
          <tr>
            <td style="${bgStyle} padding:32px 32px 28px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    ${logoHtml}
                  </td>
                </tr>
                <tr>
                  <td style="padding-top:16px;">
                    ${subtitle ? `<p style="margin:0 0 4px;font-size:14px;color:rgba(255,255,255,0.8);">${subtitle}</p>` : ''}
                    <h1 style="margin:0;font-size:26px;font-weight:500;color:#FFFFFF;">${title}</h1>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>`;
};

/**
 * Email footer: notice + contact, download app, dark bar with socials.
 */
export const emailFooter = ({ organization } = {}) => {
  const logoSrc = `${baseUrl}/images/logo_white.svg`;
  return `
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#F7F7F7; overflow:hidden;">
      
          <tr>
            <td style="padding:20px 0px;"><div style="border-top:1px solid #C0C0C0;"></div></td>
          </tr>

          <!-- Download App -->
          <tr>
            <td style="padding:8px 0px 24px;">
              <p style="margin:0 0 14px;font-size:10px;text-transform:uppercase;color:#374151;letter-spacing:0.8px;font-weight:600;">Download TrustJet Today</p>
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="padding-right:10px;">
                    <a href="#" target="_blank" style="text-decoration:none;">
                      <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" height="40" style="display:block;height:40px;width:auto;" />
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>

        <!-- ===== DARK FOOTER BAR ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#222222;overflow:hidden;">
          <!-- Social icons -->
          <tr>
            <td style="padding:24px 32px 16px;">
              <table role="presentation" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <a href="https://www.linkedin.com/company/trustjetllc/" target="_blank" style="text-decoration:none;">${icon('linkedin', 20, '#FFFFFF')}</a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- Logo -->
          <tr>
            <td style="padding:0 32px 12px;">
              <a href="https://www.trustjet.ai/" target="_blank"><img src="${logoSrc}" alt="TrustJet" width="140" style="display:block;width:140px;height:auto;opacity:0.6;" /></a>
            </td>
          </tr>
          <!-- Copyright -->
          <tr>
            <td style="padding:0 32px 24px;">
              <p style="margin:0;font-size:12px;color:rgba(255,255,255,0.6);">Copyright &copy; ${new Date().getFullYear()} TrustJet. All Rights Reserved</p>
            </td>
          </tr>
        </table>`;
};
